/**
 * Crisp chat helper — programmatic open/close + segment + session:data tagging.
 *
 * The launcher bubble is hidden globally (see CrispProvider). Chat only opens
 * via explicit triggers (user menu, footer, error fallbacks, subscription
 * stuck screen). Each open call attaches a `source` segment + structured
 * session:data so support sees the entry point without asking.
 */

import { AnalyticsEvent } from '@/shared/lib/analytics/events';
import posthog from 'posthog-js';

/**
 * Where the user opened chat from. Mirrors the `source` enum in the
 * support-chat doc — keep this in sync when adding callsites.
 */
export type PlatformChatSource =
  | 'user_menu'
  | 'footer'
  | 'studio_help'
  | 'creator_program_gate'
  | 'subscription_stuck'
  | 'access_denied'
  | 'course_bundle_error'
  | 'otp_gate'
  | 'whop_callback_error'
  | 'subscription_settings'
  | 'cancel_flow'
  | 'app_error'
  | 'global_error'
  | 'not_found';

export interface CrispChatMetadata {
  receiptId?: string;
  planId?: string;
  whopPlanId?: string;
  pageContext?: string;
  cancelReason?: string;
}

const IS_DEV = process.env.NODE_ENV === 'development';
const FORCE_DEV = process.env.NEXT_PUBLIC_CRISP_FORCE === '1';
const POSTHOG_ENABLED = !IS_DEV || FORCE_DEV;

export function isCrispReady(): boolean {
  return typeof window !== 'undefined' && Array.isArray(window.$crisp);
}

function pushCrisp(...args: unknown[]): void {
  if (typeof window === 'undefined') return;
  // Crisp's queue accepts pushes before the script loads — it replays them
  // once `client.crisp.chat/l.js` boots. So we don't gate on script-ready.
  window.$crisp = window.$crisp ?? [];
  window.$crisp.push(args);
}

/**
 * Set the verified user identity. `signature` must be the HMAC-SHA256 of
 * `email` computed server-side with `CRISP_IDENTITY_SECRET` (see
 * `/api/support/crisp-identity`). Without a valid signature the conversation
 * shows as "Unverified" in the Crisp dashboard, which is how operators
 * detect impersonation attempts.
 */
export function pushCrispIdentity(input: {
  email: string;
  signature: string;
  nickname?: string;
}): void {
  pushCrisp('set', 'user:email', [input.email, input.signature]);
  if (input.nickname) {
    pushCrisp('set', 'user:nickname', [input.nickname]);
  }
}

export function resetCrispSession(): void {
  pushCrisp('do', 'session:reset');
}

export function hideCrispLauncher(): void {
  pushCrisp('do', 'chat:hide');
}

/**
 * Open chat from a tagged surface. Sets:
 *  - `session:segments` — visible chips in the operator dashboard.
 *    `['platform', auth-state, source]` lets support filter/route at a glance.
 *  - `session:data` — key/value rows in the side panel for receipt/plan
 *    context (replaces what mailto?body= used to carry).
 *  - PostHog `platform_chat_opened` event with the same context for funnel
 *    analysis.
 */
export function openCrispChat(
  source: PlatformChatSource,
  metadata: CrispChatMetadata = {},
  options: { isAuthenticated?: boolean } = {}
): void {
  if (typeof window === 'undefined') return;

  const authSegment = options.isAuthenticated ? 'authenticated' : 'guest';
  pushCrisp('set', 'session:segments', [['platform', authSegment, source]]);

  const dataPairs: Array<[string, string]> = [
    ['surface', 'platform'],
    ['source', source],
  ];
  if (metadata.receiptId) dataPairs.push(['receipt_id', metadata.receiptId]);
  if (metadata.planId) dataPairs.push(['plan_id', metadata.planId]);
  if (metadata.whopPlanId) dataPairs.push(['whop_plan_id', metadata.whopPlanId]);
  if (metadata.pageContext) dataPairs.push(['page_context', metadata.pageContext]);
  pushCrisp('set', 'session:data', [dataPairs]);

  // chat:show is required — chat:hide also gates chat:open, so the launcher
  // must be "available" for the panel to render. The launcher is suppressed
  // visually via CSS in globals.css (.crisp-launcher hidden rule) so this
  // doesn't bring back the floating bubble.
  pushCrisp('do', 'chat:show');
  pushCrisp('do', 'chat:open');

  if (POSTHOG_ENABLED) {
    posthog.capture(AnalyticsEvent.PLATFORM_CHAT_OPENED, {
      source,
      chat_provider: 'crisp',
      ...(metadata.receiptId !== undefined && { receipt_id: metadata.receiptId }),
      ...(metadata.planId !== undefined && { plan_id: metadata.planId }),
      ...(metadata.whopPlanId !== undefined && { whop_plan_id: metadata.whopPlanId }),
      ...(metadata.pageContext !== undefined && { page_context: metadata.pageContext }),
    });
  }
}

export function closeCrispChat(): void {
  pushCrisp('do', 'chat:close');
  pushCrisp('do', 'chat:hide');
}

export type CrispEventColor = 'red' | 'orange' | 'green' | 'blue' | 'purple' | 'grey' | 'black';

/**
 * Append a moment to the user's Crisp profile timeline (visible to support
 * in the side panel before the user types). Use sparingly — only for
 * events a support agent would want to see at a glance, not for analytics.
 * PostHog remains the analytics firehose.
 *
 * Color convention:
 *  - red: incident / blocker (activation_stuck, payment_failed)
 *  - orange: caution (canceled, refund_requested)
 *  - green: success (payment_completed, course_purchased)
 *  - blue: lifecycle (account_created)
 *  - purple: bonus (redeem_code_applied)
 */
export function pushCrispEvent(
  name: string,
  data: Record<string, string | number | boolean> = {},
  color: CrispEventColor = 'grey'
): void {
  if (!POSTHOG_ENABLED) return;
  pushCrisp('set', 'session:event', [[[name, data, color]]]);
}
